import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService, { ExportFlowError } from '../services/apiService';
import {
    ExportConfirmStep,
    ExportDestination,
    ExportField,
    ExportFieldValue,
    ExportFlowAction,
    ExportFlowErrorBody,
    ExportPickStep,
    ExportResultItem,
    ExportStep,
    ExportStepEvent,
    ExportStepResponse,
    ExportSubmitResult,
    ExportWebSessionCreated,
} from '../types';
import ExportFlowField from './ExportFlowField';
import {
    canRenderStep,
    fieldInputs,
    fieldProblem,
    initialFieldValues,
    isHttpsUrl,
    newIdempotencyKey,
    openHttpsUrl,
} from '../utils/exportFlow';
import {
    closePendingWindow,
    exportReturnUrl,
    listenForWebSessionReturn,
    navigatePendingWindow,
    openPendingWindow,
} from '../utils/exportWebWindow';
import { logger } from '../utils/logger';

/**
 * Wizard voor één bestemming van /export/v2. De server bepaalt de stappen;
 * deze component kent alleen staptypes, veldtypes en acties. Terug doet de
 * wizard zelf met de bewaarde antwoorden. Versturen gebeurt nooit vanzelf
 * opnieuw: na een time-out of zonder antwoord vraagt hij de stand op.
 */

interface ExportWizardProps {
    apiService: ApiService;
    fileId: string;
    /** De ref waarmee de bestemmingen zijn opgehaald; de state hoort daarbij */
    summaryRef: string;
    destination: ExportDestination;
    /** Meteen de webstap (bestemming met web_only of actie web) */
    startWithWeb?: boolean;
    /** Bij startWithWeb: het venster dat de klik al opende (null = tegengehouden) */
    initialWindow?: Window | null;
    /** Terug naar de lijst met bestemmingen */
    onBack: () => void;
    onClose: () => void;
    /** Bestemmingen opnieuw ophalen (config_stale, unavailable); met de melding van de server */
    onDestinationsStale: (message: string) => void;
    /** Schrijft de autosave van de editor weg; false = niet gelukt */
    saveSummary?: () => Promise<boolean>;
}

interface Entry {
    response: ExportStepResponse;
    /** Niet als stap te tonen: webstap, onbekende stap, bevestiging met auto_submit */
    hidden: boolean;
    values: Record<string, ExportFieldValue>;
    createValues: Record<string, ExportFieldValue>;
    createOpen: boolean;
    /** De gebruiker veranderde de zoekvelden sinds de laatste zoekopdracht */
    searchDirty: boolean;
}

interface Notice {
    message: string;
    field?: string;
    action?: ExportFlowAction;
    retry?: boolean;
}

type View =
    | { kind: 'loading' }
    | { kind: 'step' }
    | { kind: 'start-error'; message: string; action?: ExportFlowAction; retry: boolean }
    | { kind: 'web-intro'; message: string; button: string }
    | { kind: 'web-pending'; url: string; blocked: boolean; closed: boolean }
    | { kind: 'web-ended'; reason: 'cancelled' | 'expired' }
    | { kind: 'sending'; slow: boolean }
    | { kind: 'success'; message: string; url: string | null }
    | { kind: 'failed'; message: string; action?: ExportFlowAction; retry: 'send' | 'web' | null }
    | { kind: 'unknown'; message: string | null; retrySafe: boolean };

const POLL_INTERVAL_MS = 3000;
// Zo lang vraagt de wizard de stand van een verzending op voordat hij zegt dat de uitkomst onbekend is
const ATTEMPT_POLL_WINDOW_MS = 2 * 60 * 1000;
const WEB_SESSION_FALLBACK_MS = 15 * 60 * 1000;
const DEFAULT_DEBOUNCE_MS = 400;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const stepFields = (step: ExportStep): ExportField[] => {
    if (step.kind === 'form') return step.fields;
    if (step.kind === 'confirm') return step.fields ?? [];
    if (step.kind === 'pick') return step.search?.fields ?? [];
    return [];
};

const makeEntry = (response: ExportStepResponse): Entry => {
    const renderable = canRenderStep(response.step);
    const step = response.step;
    const hidden = !renderable || step.kind === 'web' || (step.kind === 'confirm' && step.auto_submit === true);
    return {
        response,
        hidden,
        values: renderable ? initialFieldValues(stepFields(step)) : {},
        createValues: renderable && step.kind === 'pick' && step.create ? initialFieldValues([step.create.field]) : {},
        createOpen: false,
        searchDirty: false,
    };
};

const primaryButtonClass = 'px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryButtonClass = 'px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50';

const Spinner: React.FC<{ label: string; hint?: string }> = ({ label, hint }) => (
    <div role="status" className="flex flex-col items-center py-8 text-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4" />
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
);

const ExportWizard: React.FC<ExportWizardProps> = ({
    apiService, fileId, summaryRef, destination, startWithWeb, initialWindow,
    onBack, onClose, onDestinationsStale, saveSummary,
}) => {
    const { t } = useTranslation();
    const configId = destination.config_id;
    const [entries, setEntries] = useState<Entry[]>([]);
    const entriesRef = useRef<Entry[]>([]);
    const [view, setView] = useState<View>({ kind: 'loading' });
    const [notice, setNotice] = useState<Notice | null>(null);
    const [busy, setBusy] = useState<'step' | 'search' | null>(null);

    const mountedRef = useRef(true);
    // Elke lange handeling (versturen, stand opvragen, webstap) krijgt een
    // nummer; een nieuwe handeling of Terug maakt de oude ongeldig
    const runRef = useRef(0);
    const stepSeqRef = useRef(0);
    const lastRequestRef = useRef<(() => void) | null>(null);
    const submitRef = useRef<{ state: string; inputs: Record<string, ExportFieldValue> } | null>(null);
    const attemptKeyRef = useRef<string | null>(null);
    const webWindowRef = useRef<Window | null>(null);

    const alive = (run: number) => mountedRef.current && run === runRef.current;

    const updateEntries = (update: (previous: Entry[]) => Entry[]) => {
        entriesRef.current = update(entriesRef.current);
        setEntries(entriesRef.current);
    };

    const updateTop = (patch: (top: Entry) => Entry) => updateEntries((previous) => (
        previous.length === 0 ? previous : [...previous.slice(0, -1), patch(previous[previous.length - 1])]
    ));

    const ensureSaved = async (): Promise<boolean> => {
        if (!saveSummary) return true;
        try {
            if (await saveSummary()) return true;
        } catch (error) {
            logger.error('Saving the report before the export failed:', error);
        }
        return false;
    };

    const showSuccess = (result: ExportSubmitResult | undefined) => {
        setView({ kind: 'success', message: result?.display?.message || t('exportWizard.done'), url: result?.display?.url ?? null });
    };

    // --- Webstap ---

    const isBackendUrl = (url: string) => {
        const backend = apiService.resolveBackendUrl('/');
        return Boolean(backend && url.startsWith(backend));
    };

    const watchWebSession = async (sessionId: string, expiresAt: string, run: number) => {
        // Een expires_at in het verleden wijst op een scheve klok: dan de geldigheid van een webstap aanhouden
        const parsed = Date.parse(expiresAt);
        const expires = parsed > Date.now() ? parsed : Date.now() + WEB_SESSION_FALLBACK_MS;
        // Wachten tot de volgende vraag; de terugkeerpagina of terugkomen in dit tabblad maakt eerder wakker
        const waker: { wake: (() => void) | null } = { wake: null };
        const nextPoll = () => new Promise<void>((resolve) => {
            const timer = setTimeout(resolve, POLL_INTERVAL_MS);
            waker.wake = () => {
                clearTimeout(timer);
                resolve();
            };
        });
        const stopListening = listenForWebSessionReturn((message) => {
            if (message.sessionId === sessionId) waker.wake?.();
        });
        const onFocus = () => waker.wake?.();
        window.addEventListener('focus', onFocus);
        let reportedClosed = false;
        try {
            while (alive(run)) {
                await nextPoll();
                waker.wake = null;
                if (!alive(run)) return;
                const windowClosed = Boolean(webWindowRef.current?.closed);
                try {
                    const status = await apiService.getExportWebSession(sessionId);
                    if (!alive(run)) return;
                    if (status.status === 'done') {
                        showSuccess(status.result);
                        return;
                    }
                    if (status.status === 'cancelled' || status.status === 'expired') {
                        setView({ kind: 'web-ended', reason: status.status });
                        return;
                    }
                } catch (error) {
                    if (!alive(run)) return;
                    if (error instanceof ExportFlowError && error.code === 'not_found') {
                        setView({ kind: 'web-ended', reason: 'expired' });
                        return;
                    }
                    // Verbinding weg: gewoon verder vragen
                }
                if (Date.now() > expires) {
                    setView({ kind: 'web-ended', reason: 'expired' });
                    return;
                }
                if (windowClosed && !reportedClosed) {
                    reportedClosed = true;
                    setView((current) => (current.kind === 'web-pending' ? { ...current, closed: true } : current));
                }
            }
        } finally {
            stopListening();
            window.removeEventListener('focus', onFocus);
        }
    };

    const handleWebError = (error: unknown) => {
        const body = error instanceof ExportFlowError ? error.body : null;
        if (body && (body.error_code === 'config_stale' || body.error_code === 'unavailable')) {
            onDestinationsStale(body.error);
            return;
        }
        if (!body) logger.error('Starting the export web step failed with status:', error instanceof ExportFlowError ? error.status : error);
        setView({
            kind: 'failed',
            message: body?.error || t(error instanceof ExportFlowError && error.noResponse ? 'exportWizard.networkError' : 'exportWizard.genericError'),
            action: body?.action?.type === 'web' ? undefined : body?.action,
            retry: body ? null : 'web',
        });
    };

    /** Moet in de klik zelf starten (of een al geopend venster krijgen), anders houdt de popupblokker het venster tegen. */
    const startWeb = async (preopened?: Window | null) => {
        const run = ++runRef.current;
        stepSeqRef.current += 1;
        setBusy(null);
        setNotice(null);
        const win = preopened !== undefined
            ? preopened
            : openPendingWindow(t('exportWizard.web.windowTitle'), t('exportWizard.web.windowText'));
        // Een venster dat de klik in de lijst opende, sluit alleen een handeling die het ook gebruikt
        const abandon = () => {
            if (preopened === undefined) closePendingWindow(win);
        };
        setView({ kind: 'loading' });
        const saved = await ensureSaved();
        if (!alive(run)) {
            abandon();
            return;
        }
        if (!saved) {
            closePendingWindow(win);
            setView({ kind: 'failed', message: t('exportWizard.saveFailed'), retry: 'web' });
            return;
        }
        try {
            let created: ExportWebSessionCreated;
            try {
                created = await apiService.createExportWebSession(fileId, summaryRef, configId, exportReturnUrl());
            } catch (error) {
                // Deze origin staat niet in de toegestane lijst: dan zonder callback, de wizard vraagt de stand toch op
                if (!(error instanceof ExportFlowError && error.code === 'validation' && error.body?.field === 'return_url')) throw error;
                created = await apiService.createExportWebSession(fileId, summaryRef, configId);
            }
            if (!alive(run)) {
                abandon();
                return;
            }
            if (!isHttpsUrl(created.url) && !isBackendUrl(created.url)) {
                throw new Error('Unexpected web step url');
            }
            if (win && !win.closed) navigatePendingWindow(win, created.url);
            webWindowRef.current = win && !win.closed ? win : null;
            setView({ kind: 'web-pending', url: created.url, blocked: !webWindowRef.current, closed: false });
            void watchWebSession(created.session_id, created.expires_at, run);
        } catch (error) {
            if (!alive(run)) {
                abandon();
                return;
            }
            closePendingWindow(win);
            handleWebError(error);
        }
    };

    // --- Stappen ---

    const present = (response: ExportStepResponse) => {
        const entry = makeEntry(response);
        updateEntries((previous) => [...previous, entry]);
        setNotice(null);
        if (!canRenderStep(response.step)) {
            // Een staptype of veld dat de wizard niet kent: de webstap rondt de export af
            setView({ kind: 'web-intro', message: t('exportWizard.web.fallbackMessage'), button: t('exportWizard.web.fallbackButton') });
            return;
        }
        const step = response.step;
        if (step.kind === 'web') {
            setView({ kind: 'web-intro', message: step.message, button: step.button });
            return;
        }
        if (step.kind === 'confirm' && step.auto_submit) {
            // De keuze was de bevestiging
            void submit(entry);
            return;
        }
        setView({ kind: 'step' });
    };

    const handleStepError = (error: unknown, first: boolean, retry?: () => void) => {
        const flowError = error instanceof ExportFlowError ? error : null;
        const body = flowError?.body ?? null;
        if (!body) {
            logger.error('Export step failed with status:', flowError ? flowError.status : error);
            const message = t(flowError?.noResponse ? 'exportWizard.networkError' : 'exportWizard.genericError');
            lastRequestRef.current = retry ?? null;
            if (first) setView({ kind: 'start-error', message, retry: true });
            else setNotice({ message, retry: Boolean(retry) });
            return;
        }
        switch (body.error_code) {
            case 'state_expired':
                void loadFirst(body.error);
                return;
            case 'config_stale':
            case 'unavailable':
                onDestinationsStale(body.error);
                return;
            default:
                if (first) setView({ kind: 'start-error', message: body.error, action: body.action, retry: false });
                else setNotice({ message: body.error, field: body.field, action: body.action });
        }
    };

    const loadFirst = async (carriedMessage?: string) => {
        const run = ++runRef.current;
        stepSeqRef.current += 1;
        updateEntries(() => []);
        setBusy(null);
        setNotice(null);
        setView({ kind: 'loading' });
        const saved = await ensureSaved();
        if (!alive(run)) return;
        if (!saved) {
            setView({ kind: 'start-error', message: t('exportWizard.saveFailed'), retry: true });
            return;
        }
        try {
            const response = await apiService.exportFlowStep(fileId, summaryRef, configId);
            if (!alive(run)) return;
            present(response);
            if (carriedMessage) setNotice({ message: carriedMessage });
        } catch (error) {
            if (alive(run)) handleStepError(error, true);
        }
    };

    const requestStep = async (event: ExportStepEvent, mode: 'push' | 'replace') => {
        const top = entriesRef.current[entriesRef.current.length - 1];
        if (!top) return;
        const seq = ++stepSeqRef.current;
        const retry = () => { void requestStep(event, mode); };
        setBusy(mode === 'push' ? 'step' : 'search');
        setNotice(null);
        if (event.type === 'search') updateTop((entry) => ({ ...entry, searchDirty: false }));
        const done = () => {
            if (mountedRef.current && seq === stepSeqRef.current) setBusy(null);
        };
        const saved = await ensureSaved();
        if (!mountedRef.current || seq !== stepSeqRef.current) return;
        if (!saved) {
            setNotice({ message: t('exportWizard.saveFailed') });
            done();
            return;
        }
        try {
            const response = await apiService.exportFlowStep(fileId, summaryRef, configId, { state: top.response.state, event });
            if (!mountedRef.current || seq !== stepSeqRef.current) return;
            if (mode === 'replace' && response.step.kind === 'pick' && canRenderStep(response.step)) {
                // Zoeken of meer laden: dezelfde stap met nieuwe resultaten; wat de gebruiker intussen typte blijft staan
                updateTop((entry) => ({ ...entry, response }));
            } else {
                if (mode === 'replace') updateEntries((previous) => previous.slice(0, -1));
                present(response);
            }
        } catch (error) {
            if (!mountedRef.current || seq !== stepSeqRef.current) return;
            handleStepError(error, false, retry);
        } finally {
            done();
        }
    };

    // --- Versturen ---

    const showSubmitFailure = (body: ExportFlowErrorBody) => {
        switch (body.error_code) {
            case 'external_timeout':
            case 'internal':
                setView({ kind: 'unknown', message: body.error || null, retrySafe: body.retry_safe === true });
                return;
            case 'state_expired':
                void loadFirst(body.error);
                return;
            case 'config_stale':
            case 'unavailable':
                onDestinationsStale(body.error);
                return;
            case 'validation': {
                const top = entriesRef.current[entriesRef.current.length - 1];
                if (top && !top.hidden) {
                    setNotice({ message: body.error, field: body.field });
                    setView({ kind: 'step' });
                    return;
                }
                break;
            }
            default:
                break;
        }
        setView({
            kind: 'failed',
            message: body.error || t('exportWizard.genericError'),
            action: body.action,
            retry: body.error_code === 'external_error' ? 'send' : null,
        });
    };

    const pollAttempt = async (key: string, run: number, immediate = false) => {
        setView({ kind: 'sending', slow: true });
        const deadline = Date.now() + ATTEMPT_POLL_WINDOW_MS;
        let notFound = false;
        let retrySafe = false;
        let first = true;
        while (Date.now() < deadline) {
            if (!(first && immediate)) await delay(POLL_INTERVAL_MS);
            first = false;
            if (!alive(run)) return;
            try {
                const attempt = await apiService.getExportAttempt(key);
                if (!alive(run)) return;
                notFound = false;
                retrySafe = attempt.retry_safe === true;
                if (attempt.status === 'success' && attempt.result?.success === true) {
                    showSuccess(attempt.result);
                    return;
                }
                if (attempt.status === 'failed' && attempt.result && attempt.result.success === false) {
                    showSubmitFailure(attempt.result);
                    return;
                }
                if (attempt.status === 'unknown') {
                    setView({ kind: 'unknown', message: null, retrySafe });
                    return;
                }
            } catch (error) {
                if (!alive(run)) return;
                // not_found: de verzending is (nog) niet bij Notizy aangekomen
                notFound = error instanceof ExportFlowError && error.code === 'not_found';
            }
        }
        if (!alive(run)) return;
        if (notFound) setView({ kind: 'failed', message: t('exportWizard.notReceived'), retry: 'send' });
        else setView({ kind: 'unknown', message: null, retrySafe });
    };

    const send = async (key: string) => {
        const pending = submitRef.current;
        if (!pending) return;
        const run = ++runRef.current;
        stepSeqRef.current += 1;
        setBusy(null);
        setNotice(null);
        setView({ kind: 'sending', slow: false });
        const saved = await ensureSaved();
        if (!alive(run)) return;
        if (!saved) {
            setView({ kind: 'failed', message: t('exportWizard.saveFailed'), retry: 'send' });
            return;
        }
        attemptKeyRef.current = key;
        try {
            const result = await apiService.submitExport(fileId, summaryRef, configId, pending.state, pending.inputs, key);
            if (alive(run)) showSuccess(result);
        } catch (error) {
            if (!alive(run)) return;
            const body = error instanceof ExportFlowError ? error.body : null;
            if (!body || body.error_code === 'in_progress') {
                // Geen (bruikbaar) antwoord of nog bezig: de stand opvragen, niet opnieuw versturen
                if (!body) logger.warn('Export submit gave no usable response; checking the attempt.', error instanceof ExportFlowError ? error.status : error);
                void pollAttempt(key, run);
                return;
            }
            showSubmitFailure(body);
        }
    };

    function submit(entry: Entry) {
        const step = entry.response.step as ExportConfirmStep;
        submitRef.current = { state: entry.response.state, inputs: fieldInputs(step.fields ?? [], entry.values) };
        return send(newIdempotencyKey());
    }

    // Probeer opnieuw is altijd een handeling van de gebruiker, met een nieuwe sleutel
    const retrySend = () => { void send(newIdempotencyKey()); };

    const checkAgain = () => {
        if (attemptKeyRef.current) void pollAttempt(attemptKeyRef.current, ++runRef.current, true);
    };

    // --- Terug ---

    const back = () => {
        runRef.current += 1;
        stepSeqRef.current += 1;
        setBusy(null);
        setNotice(null);
        let remaining = entriesRef.current;
        if (view.kind === 'step') remaining = remaining.slice(0, -1);
        while (remaining.length > 0 && remaining[remaining.length - 1].hidden) remaining = remaining.slice(0, -1);
        if (remaining.length === 0 || startWithWeb) {
            onBack();
            return;
        }
        updateEntries(() => remaining);
        setView({ kind: 'step' });
    };

    // --- Starten en opruimen ---

    // StrictMode start dit in development twee keer; de eerste ronde stopt dan
    // vóór er een verzoek uitgaat, omdat de opruimfunctie haar ongeldig maakt
    useEffect(() => {
        mountedRef.current = true;
        if (startWithWeb) void startWeb(initialWindow);
        else void loadFirst();
        return () => {
            mountedRef.current = false;
            runRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- één keer per bestemming
    }, []);

    // Zoeken terwijl je typt (as_you_type): na debounce_ms zonder typen
    const top = entries[entries.length - 1];
    const topStep = top && canRenderStep(top.response.step) ? top.response.step : null;
    const pickStep = topStep?.kind === 'pick' ? topStep : null;
    const searchFields = pickStep?.search?.fields ?? [];
    const searchValues = top?.values ?? {};
    const searchValid = searchFields.every((field) => fieldProblem(field, searchValues[field.key]) === null);
    const searchKey = JSON.stringify(fieldInputs(searchFields, searchValues));
    const asYouType = pickStep?.search?.mode === 'as_you_type';
    const debounceMs = pickStep?.search?.debounce_ms ?? DEFAULT_DEBOUNCE_MS;
    const searchDirty = Boolean(top?.searchDirty);
    const triggerSearch = useCallback(() => {
        void requestStep({ type: 'search', inputs: fieldInputs(searchFields, searchValues) }, 'replace');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- searchKey dekt de invoer
    }, [searchKey]);
    useEffect(() => {
        if (view.kind !== 'step' || !asYouType || !searchDirty || !searchValid) return;
        const timer = setTimeout(triggerSearch, debounceMs);
        return () => clearTimeout(timer);
    }, [view.kind, asYouType, searchDirty, searchValid, debounceMs, triggerSearch]);

    // --- Weergave ---

    const runAction = (action: ExportFlowAction | undefined | null) => {
        if (!action) return;
        if (action.type === 'open_url') openHttpsUrl(action.url);
        else if (action.type === 'web') void startWeb();
    };

    // Alleen acties die de wizard kent; sso_relogin meldt hij niet
    const actionButton = (action: ExportFlowAction | undefined | null, primary = false) => {
        if (!action || !((action.type === 'open_url' && isHttpsUrl(action.url)) || action.type === 'web')) return null;
        return (
            <button type="button" onClick={() => runAction(action)} className={primary ? primaryButtonClass : secondaryButtonClass}>
                {action.label}
            </button>
        );
    };

    const header = (title: string | null) => (
        <div>
            <p className="text-xs text-gray-500 break-words">{destination.name}</p>
            {title && <h3 className="text-sm font-semibold text-gray-900 break-words whitespace-pre-line">{title}</h3>}
        </div>
    );

    const visibleFieldKeys = new Set<string>([
        ...(topStep ? stepFields(topStep).map((field) => field.key) : []),
        ...(pickStep?.create && top?.createOpen ? [pickStep.create.field.key] : []),
    ]);
    const noticeAtTop = notice && !(notice.field && visibleFieldKeys.has(notice.field) && view.kind === 'step');
    const noticeBlock = noticeAtTop && notice ? (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 space-y-2">
            <p className="text-sm text-red-700 whitespace-pre-line">{notice.message}</p>
            {(notice.retry || notice.action) && (
                <div className="flex flex-wrap gap-2">
                    {notice.retry && lastRequestRef.current && (
                        <button type="button" onClick={() => lastRequestRef.current?.()} className={secondaryButtonClass}>
                            {t('exportWizard.retry')}
                        </button>
                    )}
                    {actionButton(notice.action)}
                </div>
            )}
        </div>
    ) : null;

    const serverErrorFor = (key: string) => (notice?.field === key && view.kind === 'step' ? notice.message : null);

    const setValue = (key: string, value: ExportFieldValue, search = false) => {
        if (notice?.field === key) setNotice(null);
        updateTop((entry) => ({ ...entry, values: { ...entry.values, [key]: value }, searchDirty: search || entry.searchDirty }));
    };

    const renderFields = (fields: ExportField[], values: Record<string, ExportFieldValue>, prefix: string, onChange: (key: string, value: ExportFieldValue) => void, disabled: boolean) => (
        fields.map((field, index) => (
            <ExportFlowField
                key={field.key}
                field={field}
                value={values[field.key]}
                onChange={(value) => onChange(field.key, value)}
                idPrefix={prefix}
                serverError={serverErrorFor(field.key)}
                disabled={disabled}
                autoFocus={index === 0 && field.type === 'text'}
            />
        ))
    );

    const backButton = (
        <button type="button" onClick={back} className={secondaryButtonClass}>{t('exportWizard.back')}</button>
    );

    const renderResult = (item: ExportResultItem) => {
        const link = isHttpsUrl(item.url) ? item.url : null;
        const body = (
            <>
                <span className="flex items-start gap-2">
                    <span className="block font-medium text-gray-900 text-sm break-words">{item.title}</span>
                    {item.preselected && (
                        <span className="ml-auto flex-shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700">
                            {t('exportWizard.preselected')}
                        </span>
                    )}
                </span>
                {item.subtitle && <span className="block text-gray-500 text-xs break-words">{item.subtitle}</span>}
                {item.detail && <span className="block text-gray-500 text-xs break-words whitespace-pre-line">{item.detail}</span>}
                {!item.selectable && item.unavailable_reason && (
                    <span className="block text-gray-500 text-xs italic break-words">{item.unavailable_reason}</span>
                )}
            </>
        );
        const base = 'flex-1 min-w-0 p-3 rounded-lg border text-left transition-colors';
        return (
            <li key={item.token} className="flex items-stretch gap-2">
                {item.selectable ? (
                    <button
                        type="button"
                        onClick={() => { void requestStep({ type: 'select', token: item.token }, 'push'); }}
                        disabled={busy === 'step'}
                        className={`${base} ${item.preselected ? 'border-primary-300 bg-primary-50/40' : 'border-gray-200'} hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50`}
                    >
                        {body}
                    </button>
                ) : (
                    <div aria-disabled="true" className={`${base} border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed`}>
                        {body}
                    </div>
                )}
                {link && (
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('exportWizard.openItem')}
                        aria-label={t('exportWizard.openItem')}
                        className="flex items-center px-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                    >
                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                        </svg>
                    </a>
                )}
            </li>
        );
    };

    const renderPick = (entry: Entry, step: ExportPickStep) => {
        const disabled = busy === 'step';
        const results = step.results;
        const create = step.create;
        const createValid = create ? fieldProblem(create.field, entry.createValues[create.field.key]) === null : false;
        const searchNow = () => {
            if (!searchValid || busy) return;
            void requestStep({ type: 'search', inputs: fieldInputs(searchFields, entry.values) }, 'replace');
        };
        return (
            <div className="space-y-4">
                {header(step.title)}
                {noticeBlock}
                {step.search && step.search.fields.length > 0 && (
                    <form className="space-y-3" onSubmit={(event) => { event.preventDefault(); searchNow(); }}>
                        {renderFields(step.search.fields, entry.values, 'export-search', (key, value) => setValue(key, value, true), disabled)}
                        {step.search.mode !== 'as_you_type' && (
                            <div className="flex justify-end">
                                <button type="submit" disabled={!searchValid || Boolean(busy)} className={primaryButtonClass}>
                                    {t('exportWizard.search')}
                                </button>
                            </div>
                        )}
                    </form>
                )}
                {busy === 'search' && <p role="status" className="text-sm text-gray-500">{t('exportWizard.searching')}</p>}
                {step.notice && <p className="text-sm text-gray-700 whitespace-pre-line">{step.notice}</p>}
                {results && results.length === 0 && busy !== 'search' && (
                    <p role="status" className="text-sm text-gray-500 whitespace-pre-line">{step.empty_text || t('exportWizard.empty')}</p>
                )}
                {results && results.length > 0 && (
                    <ul className="space-y-2 max-h-72 overflow-y-auto" aria-busy={busy === 'search'}>
                        {results.map(renderResult)}
                    </ul>
                )}
                {step.truncated && <p className="text-xs text-gray-500">{t('exportWizard.truncated')}</p>}
                {step.more && (
                    <button type="button" onClick={() => { void requestStep({ type: 'more' }, 'replace'); }} disabled={Boolean(busy)} className={secondaryButtonClass}>
                        {t('exportWizard.more')}
                    </button>
                )}
                {create && !entry.createOpen && (
                    <button
                        type="button"
                        onClick={() => updateTop((current) => ({ ...current, createOpen: true }))}
                        disabled={disabled}
                        className="w-full flex items-center gap-2 p-3 rounded-lg border border-dashed border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
                    >
                        <span aria-hidden="true">+</span>
                        <span className="break-words">{create.label}</span>
                    </button>
                )}
                {create && entry.createOpen && (
                    <form
                        className="space-y-3 pt-4 border-t border-gray-200"
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (!createValid || busy) return;
                            void requestStep({ type: 'create', inputs: fieldInputs([create.field], entry.createValues) }, 'push');
                        }}
                    >
                        <ExportFlowField
                            field={create.field}
                            value={entry.createValues[create.field.key]}
                            onChange={(value) => {
                                if (notice?.field === create.field.key) setNotice(null);
                                updateTop((current) => ({ ...current, createValues: { ...current.createValues, [create.field.key]: value } }));
                            }}
                            idPrefix="export-create"
                            serverError={serverErrorFor(create.field.key)}
                            disabled={disabled}
                            autoFocus
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => updateTop((current) => ({ ...current, createOpen: false }))} className={secondaryButtonClass}>
                                {t('exportWizard.cancel')}
                            </button>
                            <button type="submit" disabled={!createValid || Boolean(busy)} className={primaryButtonClass}>
                                {create.label}
                            </button>
                        </div>
                    </form>
                )}
                <div className="flex justify-end space-x-2">{backButton}</div>
            </div>
        );
    };

    const renderStep = () => {
        if (!top || !topStep) return null;
        const disabled = busy === 'step';
        if (topStep.kind === 'pick') return renderPick(top, topStep);
        if (topStep.kind === 'form') {
            const valid = topStep.fields.every((field) => fieldProblem(field, top.values[field.key]) === null);
            return (
                <form
                    className="space-y-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (!valid || busy) return;
                        void requestStep({ type: 'submit_form', inputs: fieldInputs(topStep.fields, top.values) }, 'push');
                    }}
                >
                    {header(topStep.title)}
                    {noticeBlock}
                    {renderFields(topStep.fields, top.values, 'export-form', (key, value) => setValue(key, value), disabled)}
                    <div className="flex justify-end space-x-2">
                        {backButton}
                        <button type="submit" disabled={!valid || Boolean(busy)} className={primaryButtonClass}>{topStep.button}</button>
                    </div>
                </form>
            );
        }
        if (topStep.kind === 'confirm') {
            const fields = topStep.fields ?? [];
            const valid = fields.every((field) => fieldProblem(field, top.values[field.key]) === null);
            return (
                <form
                    className="space-y-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (valid) void submit(top);
                    }}
                >
                    {header(topStep.title)}
                    {noticeBlock}
                    {topStep.message && (topStep.style === 'warning' ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                            <p className="text-sm text-amber-900 whitespace-pre-line break-words">{topStep.message}</p>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-line break-words">{topStep.message}</p>
                    ))}
                    {renderFields(fields, top.values, 'export-confirm', (key, value) => setValue(key, value), false)}
                    <div className="flex justify-end space-x-2">
                        {backButton}
                        <button type="submit" disabled={!valid} className={primaryButtonClass}>{topStep.button}</button>
                    </div>
                </form>
            );
        }
        if (topStep.kind === 'message') {
            return (
                <div className="space-y-4">
                    {header(topStep.title)}
                    {noticeBlock}
                    <p className="text-sm text-gray-900 whitespace-pre-line break-words">{topStep.message}</p>
                    <div className="flex justify-end space-x-2">
                        {backButton}
                        {actionButton(topStep.action, true)}
                    </div>
                </div>
            );
        }
        return null;
    };

    switch (view.kind) {
        case 'loading':
            return <Spinner label={t('exportWizard.loading')} />;
        case 'step':
            return renderStep();
        case 'start-error':
            return (
                <div className="space-y-4">
                    {header(null)}
                    <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
                        <p className="text-sm text-red-700 whitespace-pre-line">{view.message}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        {backButton}
                        {view.retry && (
                            <button type="button" onClick={() => { void loadFirst(); }} className={secondaryButtonClass}>{t('exportWizard.retry')}</button>
                        )}
                        {actionButton(view.action, true)}
                    </div>
                </div>
            );
        case 'web-intro':
            return (
                <div className="space-y-4">
                    {header(null)}
                    <p className="text-sm text-gray-900 whitespace-pre-line break-words">{view.message}</p>
                    <div className="flex justify-end space-x-2">
                        {backButton}
                        <button type="button" onClick={() => { void startWeb(); }} className={primaryButtonClass}>{view.button}</button>
                    </div>
                </div>
            );
        case 'web-pending':
            return (
                <div className="space-y-4">
                    {header(t('exportWizard.web.pendingTitle'))}
                    {view.closed ? (
                        <p className="text-sm text-gray-700">{t('exportWizard.web.closed')}</p>
                    ) : view.blocked ? (
                        <p className="text-sm text-gray-700">{t('exportWizard.web.blocked')}</p>
                    ) : (
                        <div role="status" className="flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin flex-shrink-0" />
                            <p className="text-sm text-gray-700">{t('exportWizard.web.pending')}</p>
                        </div>
                    )}
                    <div className="flex flex-wrap justify-end gap-2">
                        {backButton}
                        {view.blocked && !view.closed && (
                            <a
                                href={view.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setView({ ...view, blocked: false })}
                                className={primaryButtonClass}
                            >
                                {t('exportWizard.web.openWindow')}
                            </a>
                        )}
                        {view.closed && (
                            <button type="button" onClick={() => { void startWeb(); }} className={primaryButtonClass}>{t('exportWizard.web.reopen')}</button>
                        )}
                    </div>
                </div>
            );
        case 'web-ended':
            return (
                <div className="space-y-4">
                    {header(null)}
                    <p className="text-sm text-gray-700">{t(`exportWizard.web.${view.reason}`)}</p>
                    <div className="flex justify-end space-x-2">
                        {backButton}
                        <button type="button" onClick={() => { void startWeb(); }} className={primaryButtonClass}>{t('exportWizard.web.reopen')}</button>
                    </div>
                </div>
            );
        case 'sending':
            return view.slow
                ? <Spinner label={t('exportWizard.sendingSlow')} hint={t('exportWizard.sendingSlowHint')} />
                : <Spinner label={t('exportWizard.sending')} />;
        case 'success':
            return (
                <div className="flex flex-col items-center py-6 text-center">
                    <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <p role="status" className="text-sm text-gray-900 whitespace-pre-line break-words">{view.message}</p>
                    <div className="flex space-x-2 mt-4">
                        {isHttpsUrl(view.url) && (
                            <button type="button" onClick={() => openHttpsUrl(view.url)} className={primaryButtonClass}>{t('exportWizard.open')}</button>
                        )}
                        <button type="button" onClick={onClose} className={secondaryButtonClass}>{t('exportWizard.close')}</button>
                    </div>
                </div>
            );
        case 'failed':
            return (
                <div className="space-y-4">
                    {header(null)}
                    <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
                        <p className="text-sm text-red-700 whitespace-pre-line break-words">{view.message}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        {backButton}
                        {view.retry === 'send' && (
                            <button type="button" onClick={retrySend} className={primaryButtonClass}>{t('exportWizard.retry')}</button>
                        )}
                        {view.retry === 'web' && (
                            <button type="button" onClick={() => { void startWeb(); }} className={primaryButtonClass}>{t('exportWizard.retry')}</button>
                        )}
                        {actionButton(view.action, view.retry === null)}
                    </div>
                </div>
            );
        case 'unknown':
            return (
                <div className="space-y-4">
                    {header(t('exportWizard.unknownTitle'))}
                    <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-1">
                        <p className="text-sm text-amber-900 whitespace-pre-line break-words">{view.message || t('exportWizard.unknownMessage')}</p>
                        <p className="text-sm text-amber-900">{t('exportWizard.unknownCheck')}</p>
                        {!view.retrySafe && <p className="text-sm font-medium text-amber-900">{t('exportWizard.unknownDuplicate')}</p>}
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        <button type="button" onClick={onClose} className={secondaryButtonClass}>{t('exportWizard.close')}</button>
                        <button type="button" onClick={checkAgain} className={secondaryButtonClass}>{t('exportWizard.checkAgain')}</button>
                        <button type="button" onClick={retrySend} className={secondaryButtonClass}>{t('exportWizard.retry')}</button>
                    </div>
                </div>
            );
    }
};

export default ExportWizard;
